import {
  Button,
  Flex,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import iconClose from "../../assets/images/icon-login.png";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { InputControl } from "../form/formControls";

export const MASTER_PASSWORD_MODE = {
  UPDATE: "update",
  CLEAR: "clear",
};

const DEFAULT_VALUES = {
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
};

/**
 * Popup đặt / đổi / xoá mật khẩu hỗ trợ đăng nhập.
 *
 * Hệ thống không trả về mật khẩu đã lưu nên các ô luôn để trống, mỗi lần mở lại
 * là nhập mới. Đã có mật khẩu thì bắt buộc nhập đúng mật khẩu hiện tại.
 *
 * @param {Boolean} isSet     Đã thiết lập mật khẩu hỗ trợ hay chưa
 * @param {String}  mode      "update" (đặt / đổi) hoặc "clear" (xoá)
 * @param {Number}  minLength Độ dài tối thiểu do backend quy định
 */
const PopupMasterPassword = ({
  isOpen,
  onClose,
  isSet,
  mode = MASTER_PASSWORD_MODE.UPDATE,
  minLength = 6,
  isLoading,
  onSubmit,
}) => {
  const isClearMode = mode === MASTER_PASSWORD_MODE.CLEAR;

  const schema = useMemo(
    () =>
      Yup.object().shape({
        currentPassword: isSet
          ? Yup.string().required("Vui lòng nhập mật khẩu hỗ trợ hiện tại")
          : Yup.string().notRequired(),
        newPassword: isClearMode
          ? Yup.string().notRequired()
          : Yup.string()
              .required("Vui lòng nhập mật khẩu hỗ trợ mới")
              .min(
                minLength,
                `Mật khẩu hỗ trợ phải có ít nhất ${minLength} ký tự`,
              ),
        confirmNewPassword: isClearMode
          ? Yup.string().notRequired()
          : Yup.string()
              .oneOf(
                [Yup.ref("newPassword"), null],
                "Nhập lại mật khẩu mới chưa khớp",
              )
              .required("Vui lòng nhập lại mật khẩu hỗ trợ mới"),
      }),
    [isSet, isClearMode, minLength],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  const [focusStates, setFocusStates] = useState({});

  // Mở lại popup là xoá sạch ô nhập, không giữ lại mật khẩu vừa gõ
  useEffect(() => {
    reset(DEFAULT_VALUES);
    setFocusStates({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]);

  const handleFocus = (field) => {
    setFocusStates((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setFocusStates((prev) => ({ ...prev, [field]: false }));
  };

  const submitHandler = (data) => {
    onSubmit(data);
  };

  const title = isClearMode
    ? "Xoá mật khẩu hỗ trợ"
    : isSet
      ? "Đổi mật khẩu hỗ trợ"
      : "Đặt mật khẩu hỗ trợ";

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW={"625px"}>
        <Flex
          alignItems={"center"}
          justifyContent={"space-between"}
          p={"25px 24px 24px 24px"}
          borderBottom={"1px solid var(--color-boder)"}
        >
          <Text
            fontSize={"18px"}
            fontWeight={600}
            lineHeight={"normal"}
            color="var(--color-black)"
          >
            {title}
          </Text>
          <Image src={iconClose} cursor={"pointer"} onClick={onClose} />
        </Flex>

        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex p={"20px 40px"} flexDirection={"column"} gap={"20px"}>
            <Text fontSize={"13px"} color={"var(--color-label-employee)"}>
              {isClearMode
                ? "Xoá xong sẽ không đăng nhập bằng mật khẩu hỗ trợ được nữa, mật khẩu riêng của từng nhân viên không bị ảnh hưởng."
                : "Mật khẩu này đăng nhập được vào mọi tài khoản để kiểm tra lỗi hộ nhân viên, mật khẩu riêng của nhân viên vẫn dùng bình thường. Hệ thống không hiển thị lại mật khẩu đã lưu và mỗi lần thay đổi đều được ghi nhật ký ở máy chủ."}
            </Text>

            {!!isSet && (
              <InputControl
                type="password"
                label="Mật khẩu hỗ trợ hiện tại"
                placeholder="Nhập mật khẩu hỗ trợ hiện tại"
                errors={errors.currentPassword}
                register={register("currentPassword")}
                onFocus={() => handleFocus("currentPassword")}
                onBlur={() => handleBlur("currentPassword")}
                state={focusStates["currentPassword"]}
                mwidth={"100%"}
              />
            )}

            {!isClearMode && (
              <>
                <InputControl
                  type="password"
                  label="Mật khẩu hỗ trợ mới"
                  placeholder="Nhập mật khẩu hỗ trợ mới"
                  errors={errors.newPassword}
                  register={register("newPassword")}
                  onFocus={() => handleFocus("newPassword")}
                  onBlur={() => handleBlur("newPassword")}
                  state={focusStates["newPassword"]}
                  mwidth={"100%"}
                />
                <InputControl
                  type="password"
                  label="Nhập lại mật khẩu hỗ trợ mới"
                  placeholder="Nhập lại mật khẩu hỗ trợ mới"
                  errors={errors.confirmNewPassword}
                  register={register("confirmNewPassword")}
                  onFocus={() => handleFocus("confirmNewPassword")}
                  onBlur={() => handleBlur("confirmNewPassword")}
                  state={focusStates["confirmNewPassword"]}
                  mwidth={"100%"}
                />
              </>
            )}
          </Flex>

          <Flex
            justifyContent={"flex-end"}
            gap={"12px"}
            p={"0px 40px 24px 40px"}
          >
            <Button
              type="button"
              variant={"outline"}
              borderRadius={"8px"}
              w={"120px"}
              h={"44px"}
              onClick={onClose}
            >
              Huỷ
            </Button>
            <Button
              type="submit"
              isLoading={isLoading}
              borderRadius={"8px"}
              w={"164px"}
              h={"44px"}
              {...(isClearMode
                ? { colorScheme: "red" }
                : {
                    background: "var(--linear-gradient-employee)",
                    color: "var(--color-main)",
                  })}
              _hover={{
                filter: "brightness(90%)",
              }}
              _active={{
                filter: "brightness(90%)",
              }}
            >
              {isClearMode ? "Xoá mật khẩu" : isSet ? "Đổi mật khẩu" : "Đặt mật khẩu"}
            </Button>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default PopupMasterPassword;
